/**
 * Market Signal and the Contracts board (GDD §3.9–3.10).
 *
 * Market prices are a pure function of the game clock and the resource id —
 * every player on the same period sees the same market, which a future server
 * can reproduce exactly.
 */

import {
  CONTRACT_QTY_MAX, CONTRACT_QTY_MIN, CONTRACT_REFILL, MARKET_HOT, MARKET_MAX, MARKET_MIN, MARKET_PERIOD,
} from '../content/market';
import { resource } from '../content/resources';
import { nextRandom } from './rng';
import { ADDITIONS } from '../content/flags';
import { roomMult } from './rooms';
import type { Contract, GameState } from './state';

function hash01(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) / 4294967296;
}

export function marketPeriod(state: GameState): number {
  return Math.floor(state.clock / MARKET_PERIOD);
}

export function marketSecondsLeft(state: GameState): number {
  return MARKET_PERIOD - (state.clock % MARKET_PERIOD);
}

/** This period's Hot Contract: one discovered alloy or item paying ×2.5. */
export function hotResource(state: GameState): string | null {
  const pool = state.discovered.filter((id) => resource(id).kind !== 'ore').sort();
  if (pool.length === 0) return null;
  return pool[Math.floor(hash01(`${marketPeriod(state)}:hot`) * pool.length)]!;
}

/** IPM's Market Manipulation: the top few percent of rolls become ×4. */
const MANIPULATION_ROLL = 0.96;
const MANIPULATED = 4;
/** IPM's Inside Trader: +15% chance of a positive swing. */
const INSIDE_TRADER = 0.15;

function multAt(state: GameState, id: string, period: number): number {
  const pool = state.discovered.filter((r) => resource(r).kind !== 'ore').sort();
  const hot = pool.length ? pool[Math.floor(hash01(`${period}:hot`) * pool.length)] : null;
  if (id === hot) return MARKET_HOT;
  const projects = state.run.projects;
  let roll = hash01(`${period}:${id}`);
  if (projects.includes('inside_trader')) roll = Math.min(1, roll + INSIDE_TRADER);
  if (projects.includes('market_manipulation') && roll >= MANIPULATION_ROLL) return 1 + (MANIPULATED - 1) * roomMult(state, 'market');
  const raw = MARKET_MIN + (MARKET_MAX - MARKET_MIN) * roll;
  return raw > 1 ? 1 + (raw - 1) * roomMult(state, 'market') : raw;
}

/** Price multiplier for a resource this period. Marketing scales the upside only. */
export function marketMult(state: GameState, id: string): number {
  return multAt(state, id, marketPeriod(state));
}

/** Next period's multiplier — what IPM's Market Insight shows ahead of time. */
export function marketForecast(state: GameState, id: string): number {
  return multAt(state, id, marketPeriod(state) + 1);
}

// ── Contracts ──────────────────────────────────────────────────────────────

const RARITY_QTY: Readonly<Record<string, readonly [number, number]>> = {
  alloy: [CONTRACT_QTY_MIN * 2, CONTRACT_QTY_MAX * 2],
  common: [CONTRACT_QTY_MIN, CONTRACT_QTY_MAX],
  rare: [1, 4],
  void: [1, 1],
};

function newContract(state: GameState): Contract | null {
  // Ask for something from the newest few discoveries, so contracts track progress.
  const recent = state.discovered.filter((id) => resource(id).kind !== 'ore').slice(-8);
  if (recent.length === 0) return null;
  // Avoid asking for something already on the board while there is any alternative.
  const onBoard = new Set(state.contracts.filter((c) => c).map((c) => c!.item));
  const fresh = recent.filter((id) => !onBoard.has(id));
  const pool = fresh.length ? fresh : recent;
  const item = pool[Math.floor(nextRandom(state) * pool.length)]!;
  const def = resource(item);
  const [lo, hi] = RARITY_QTY[def.kind === 'alloy' ? 'alloy' : def.rarity!]!;
  const qty = lo + Math.floor(nextRandom(state) * (hi - lo + 1));
  const vs = Math.max(2, Math.round(Math.log10(def.value * qty) * 2.5));
  return { id: `ct${state.nextId++}`, item, qty, vs };
}

/** Fills empty slots whose refill time has come. */
export function stepContracts(state: GameState): void {
  if (!ADDITIONS.contracts) return;
  state.contracts.forEach((c, i) => {
    if (c === null && state.clock >= (state.contractReadyAt[i] ?? 0)) state.contracts[i] = newContract(state);
  });
}

export function contractReward(state: GameState, c: Contract): number {
  return Math.round(c.vs * roomMult(state, 'contractVs'));
}

export function deliverContract(state: GameState, slot: number): boolean {
  const c = state.contracts[slot];
  if (!c || (state.run.hold[c.item] ?? 0) < c.qty) return false;
  state.run.hold[c.item]! -= c.qty;
  state.vs += contractReward(state, c);
  state.lifetime.contracts += 1;
  state.contracts[slot] = null;
  state.contractReadyAt[slot] = state.clock + CONTRACT_REFILL;
  return true;
}

/** Turns a contract down; the slot refills on the normal timer. */
export function discardContract(state: GameState, slot: number): boolean {
  if (!state.contracts[slot]) return false;
  state.contracts[slot] = null;
  state.contractReadyAt[slot] = state.clock + CONTRACT_REFILL;
  return true;
}
